import React from 'react';
import { Flex, GoodsCarousel, type GoodsData, Headline } from '@banhala/ui-kit';
import {
  useAddLikeGoods,
  useLikeMap,
  useLogAnalyticsEvent,
} from 'shared/src/hooks';
import {
  bridge,
  isNotNil,
  LOG_ANALYTICS_ALL_CHANNELS,
  useNavigator,
} from 'shared/src/utils';
import type { LogAnalyticsEventParams } from 'shared/src/utils/bridge/logAnalyticsEvent/types';
import { useRemoveLikeGoods } from 'shared/src/queries';
import type { GoodsLikeRecommendItem } from 'shared/src/api';
import { AdAction } from 'shared/src/utils/eventLogger/adLogger/types';
import { useLogDisplayRecommendGoods } from './hooks/useLogDisplayRecommendGoods';

const SCREEN = 'SEARCH';

type Props = {
  isLogEnabled: boolean;
  listId?: string;
  carousel: {
    title: string;
    item_list: (GoodsData & GoodsLikeRecommendItem)[];
  };
};

// iOS ComplementaryGoodsCarousel / 추천상품 모달의 검색 보조 화면 대응.
// PDP GoodsCarouselItem 을 SCREEN=SEARCH 검색 도메인으로 분리한 형태.
export const RecommendGoodsCarouselItem = ({
  isLogEnabled,
  listId,
  carousel,
}: Props) => {
  const { logDisplayGoods } = useLogDisplayRecommendGoods({
    enabled: isLogEnabled,
  });

  const { likeMap, setLikeMap } = useLikeMap({
    initialLikeMap: carousel.item_list.reduce<Record<string, boolean>>(
      (acc, val) => {
        acc[val.item.sno] = val.item.like.is_liked;
        return acc;
      },
      {},
    ),
  });

  const { addLikeGoods } = useAddLikeGoods({
    listId: listId ?? '',
    onChangeIsLiked: (sno, nextValue) => {
      setLikeMap(prevState => ({ ...prevState, [sno]: nextValue }));
    },
    screen: SCREEN,
  });
  const { mutateAsync: mutateRemoveLikeGoods } = useRemoveLikeGoods();
  const { navigator } = useNavigator();
  const logAnalyticsEvent = useLogAnalyticsEvent();

  return (
    <Flex>
      <Headline headline={carousel.title} size={'small'} topCompact={true} />
      <Flex>
        <GoodsCarousel
          keyExtractor={(item, index) => `${item.sno}-${index}`}
          list={carousel.item_list}
          onPressItem={async (pressed, index) => {
            const { item, logging } = pressed;
            logAnalyticsEvent({
              name: 'CLICK_GOODS',
              ably_only_log: logging.ably_only_log,
              analytics: {
                ...logging.analytics,
                INDEX: index,
              },
            });
            if (isNotNil(item.ad)) {
              bridge.logAdEvent({ ad: item.ad, action: AdAction.CLICK });
            }
            navigator.moveToGoods({
              sno: item.sno,
              firstPageRendering: item.first_page_rendering,
              ad: item.ad,
            });
          }}
          likeMap={likeMap}
          onPressLike={async (pressed, index) => {
            const { item, logging } = pressed;

            const analytics: LogAnalyticsEventParams['analytics'] = {
              ...logging.analytics,
              INDEX: index,
            };

            if (!likeMap[item.sno]) {
              logAnalyticsEvent({
                name: 'CLICK_LIKE_GOODS',
                ably_only_log: logging.ably_only_log,
                channel: LOG_ANALYTICS_ALL_CHANNELS,
                analytics,
              });
              if (isNotNil(item.ad)) {
                bridge.logAdEvent({ ad: item.ad, action: AdAction.LIKE });
              }
              addLikeGoods(item.sno, {
                SCREEN,
              });
              return;
            }

            logAnalyticsEvent({
              name: 'CLICK_UNLIKE_GOODS',
              ably_only_log: logging.ably_only_log,
              channel: LOG_ANALYTICS_ALL_CHANNELS,
              analytics,
            });

            if (isNotNil(item.ad)) {
              bridge.logAdEvent({ ad: item.ad, action: AdAction.UNLIKE });
            }

            await mutateRemoveLikeGoods({
              sno: item.sno,
            });

            bridge.likeGoods({
              goods_sno: item.sno,
              is_liked: false,
            });
            setLikeMap(prevState => ({
              ...prevState,
              [item.sno]: false,
            }));
          }}
          size={'small'}
          onViewableItemsChanged={logDisplayGoods}
          viewabilityConfig={{
            itemVisiblePercentThreshold: 50,
            minimumViewTime: 500,
          }}
        />
      </Flex>
    </Flex>
  );
};
